// PATH: src/components/catalog/forms/SubjectDialog.tsx
import * as React from "react";
import { Stack, Tab, Tabs, TextField } from "@mui/material";
import { useSubjectsApi } from "../../../api";
import type { Program, Subject } from "../../../models/domain";
import { codeTaken, emptyToNull, isHexColor, nextSortOrder, orderTaken, parseOptionalInt } from "../catalogModel";
import { FormDialog } from "./FormDialog";
import { ColorField, hasVisualErrors, VisualFields, visualsFrom, visualsPayload, type VisualValues } from "./VisualFields";

export type SubjectTab = "identity" | "visuals";

interface Props {
  open: boolean;
  onClose: () => void;
  program: Program;
  /** Matières du programme (doublons de code et de rang). */
  siblings: Subject[];
  subject?: Subject | null;
  /** Onglet ouvert d'abord (« Univers 3D » depuis la petite planète). */
  initialTab?: SubjectTab;
}

/** Matière d'un programme : identité et visuels de l'univers 3D. */
export const SubjectDialog: React.FC<Props> = ({ open, onClose, program, siblings, subject, initialTab = "identity" }) => {
  const { createSubject, updateSubject } = useSubjectsApi();
  const [tab, setTab] = React.useState<SubjectTab>("identity");
  const [name, setName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [order, setOrder] = React.useState("");
  const [color, setColor] = React.useState("");
  const [visuals, setVisuals] = React.useState<VisualValues>(visualsFrom(null));
  const [touched, setTouched] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setName(subject?.name ?? "");
    setCode(subject?.code ?? "");
    setOrder(String(subject ? subject.sort_order ?? 0 : nextSortOrder(siblings)));
    setColor(subject?.color_hex ?? "");
    setVisuals(visualsFrom(subject));
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, subject?.id, initialTab]);

  const parsedOrder = parseOptionalInt(order);
  const errors = {
    name: !name.trim() ? "Le nom est requis." : "",
    code: !code.trim()
      ? "Le code est requis."
      : codeTaken(siblings, code, subject?.id)
        ? "Ce code est déjà utilisé dans ce programme."
        : "",
    order:
      parsedOrder === null
        ? "Requis."
        : Number.isNaN(parsedOrder)
          ? "Un nombre entier."
          : orderTaken(siblings, parsedOrder, subject?.id)
            ? "Rang déjà occupé."
            : "",
    color: color.trim() && !isHexColor(color) ? "Format #RRGGBB." : "",
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : "");

  const validate = () => {
    setTouched(true);
    if (errors.name || errors.code || errors.order || errors.color) {
      setTab("identity");
      return false;
    }
    if (hasVisualErrors(visuals)) {
      setTab("visuals");
      return false;
    }
    return true;
  };

  const submit = async () => {
    const payload = {
      program_id: program.id,
      name: name.trim(),
      code: code.trim(),
      sort_order: parsedOrder ?? 0,
      color_hex: emptyToNull(color),
      ...visualsPayload(visuals),
    };
    if (subject) await updateSubject(subject.id, payload);
    else await createSubject(payload);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={subject ? `Modifier ${subject.name}` : "Nouvelle matière"}
      context={`Programme : ${program.name}`}
      entity="subject"
      maxWidth="sm"
      submitLabel={subject ? "Enregistrer" : "Créer la matière"}
      successMessage={subject ? "Matière enregistrée." : "Matière créée."}
      validate={validate}
      onSubmit={submit}
      busy={busy}
    >
      <Tabs value={tab} onChange={(_, v: SubjectTab) => setTab(v)} sx={{ mb: 2.5, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="identity" label="Identité" />
        <Tab value="visuals" label="Univers 3D" />
      </Tabs>
      {tab === "identity" ? (
        <Stack spacing={1}>
          <TextField
            autoFocus
            label="Nom"
            required
            fullWidth
            value={name}
            placeholder="Mathématiques"
            onChange={(e) => setName(e.target.value)}
            error={Boolean(show("name"))}
            helperText={show("name") || " "}
            slotProps={{ htmlInput: { dir: "auto" } }}
          />
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Code"
              required
              fullWidth
              value={code}
              placeholder="mathematiques"
              onChange={(e) => setCode(e.target.value)}
              error={Boolean(show("code"))}
              helperText={show("code") || "Identifiant stable, utilisé par les imports."}
              slotProps={{ htmlInput: { className: "mono", spellCheck: false } }}
            />
            <TextField
              label="Rang"
              required
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              error={Boolean(show("order"))}
              helperText={show("order") || "Unique par programme"}
              sx={{ width: 150, flexShrink: 0 }}
              slotProps={{ htmlInput: { inputMode: "numeric" } }}
            />
          </Stack>
          <ColorField
            label="Couleur de la pastille"
            value={color}
            onChange={setColor}
            error={show("color")}
            helperText="Repère la matière dans les listes. Vide = couleur principale de l'univers 3D."
          />
        </Stack>
      ) : (
        <VisualFields
          value={visuals}
          onChange={setVisuals}
          uploadPath="embed/subjects"
          showErrors={touched}
          onBusyChange={setBusy}
        />
      )}
    </FormDialog>
  );
};

export default SubjectDialog;
